import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Fish, ExternalLink } from "lucide-react";

const THRESHOLDS = [
  { label: "$100k+", value: 100000 },
  { label: "$500k+", value: 500000 },
  { label: "$1M+", value: 1000000 },
  { label: "$5M+", value: 5000000 },
];

const EXPLORERS = {
  1: "https://etherscan.io/tx/",
  56: "https://bscscan.com/tx/",
  137: "https://polygonscan.com/tx/",
  42161: "https://arbiscan.io/tx/",
  10: "https://optimistic.etherscan.io/tx/",
  8453: "https://basescan.org/tx/",
  43114: "https://snowtrace.io/tx/",
};

function timeAgo(ts) {
  const diff = Math.floor(Date.now() / 1000) - ts;
  if (diff < 60) return diff + "s ago";
  if (diff < 3600) return Math.floor(diff / 60) + "m ago";
  return Math.floor(diff / 3600) + "h ago";
}

function short(addr) {
  return addr.slice(0, 6) + "..." + addr.slice(-4);
}

export default function WhaleTracker() {
  const [txs, setTxs] = useState([]);
  const [minUsd, setMinUsd] = useState(250000);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const currentThreshold = minUsd;

    const load = async () => {
      try {
        const { data } = await api.get("/tools/whale-transactions", { params: { min_usd: currentThreshold } });
        if (!cancelled && currentThreshold === minUsd) {
          setTxs(data.transactions || []);
        }
      } catch {
        /* stays empty on failure */
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    setLoading(true);
    load();
    const interval = setInterval(load, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [minUsd]);

  return (
    <div className="pt-28 pb-20 max-w-3xl mx-auto px-4 sm:px-6">
      <div className="text-center mb-10">
        <Fish className="w-8 h-8 text-yellow-400 mx-auto mb-4" />
        <h1 className="font-display font-bold text-4xl text-[var(--text-primary)]">Whale Tracker</h1>
        <p className="mt-3 text-[var(--text-secondary)]">
          Live large transfers across Ethereum, Polygon, and Arbitrum, covering both native currency and major stablecoins (USDT, USDC).
        </p>
      </div>

      <div className="flex items-center gap-2 mb-6 flex-wrap justify-center">
        {THRESHOLDS.map((t) => (
          <button key={t.value} onClick={() => setMinUsd(t.value)}
            className={"px-3 py-1.5 text-xs font-mono uppercase tracking-widest border transition " +
              (minUsd === t.value ? "bg-yellow-400 text-black border-yellow-400" : "border-[var(--border-soft)] text-[var(--text-tertiary)] hover:border-yellow-400 hover:text-yellow-400")}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-center text-[var(--text-tertiary)] font-mono text-sm">Scanning latest blocks...</p>
      ) : txs.length === 0 ? (
        <div className="text-center py-10 border border-[var(--border-soft)] bg-[var(--bg-panel)]">
          <p className="text-[var(--text-tertiary)] font-mono text-sm">
            No transfers above this threshold in the latest scanned blocks.
          </p>
          <p className="text-xs text-[var(--text-tertiary)] mt-2 max-w-md mx-auto">
            The tracker checks only the newest confirmed block on each network every 30 seconds, not full history. A quiet block is normal, especially at higher thresholds. Try a lower filter or check back shortly.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {txs.map((tx) => (
            <div key={tx.hash} className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-4 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <span className="text-xs font-mono px-2 py-1 border border-yellow-400/30 bg-yellow-400/10 text-yellow-400">{tx.chain}</span>
                <div className="text-sm text-[var(--text-secondary)] font-mono">
                  {short(tx.from)} → {tx.to ? short(tx.to) : "contract creation"}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="font-mono font-bold text-[var(--text-primary)]">${tx.usd.toLocaleString()}</div>
                  <div className="text-xs font-mono text-[var(--text-tertiary)]">{tx.amount} {tx.native} · {timeAgo(tx.timestamp)}</div>
                </div>
                <a href={EXPLORERS[tx.chain_id] + tx.hash} target="_blank" rel="noopener noreferrer" className="text-[var(--text-tertiary)] hover:text-yellow-400 transition">
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="mt-8 text-xs text-[var(--text-tertiary)] text-center">
        Scans native currency and USDT/USDC transfers on Ethereum, Polygon, and Arbitrum, refreshed roughly every minute. More chains and tokens may be added later. Not financial advice.
      </p>

      <div className="mt-16 border-t border-[var(--border-soft)] pt-10">
        <h2 className="font-display font-bold text-2xl text-[var(--text-primary)] mb-6">How the Whale Tracker works</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-5">
            <div className="font-bold text-sm text-[var(--text-primary)] mb-1">1. Scan latest blocks</div>
            <p className="text-xs text-[var(--text-secondary)]">The tracker periodically pulls the newest blocks on Ethereum, Polygon, and Arbitrum, checking both native transfers and major stablecoin transfers for large amounts.</p>
          </div>
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-5">
            <div className="font-bold text-sm text-[var(--text-primary)] mb-1">2. Convert to USD</div>
            <p className="text-xs text-[var(--text-secondary)]">Each transfer's native amount is multiplied by the live market price to get its dollar value at the time of the transaction.</p>
          </div>
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-5">
            <div className="font-bold text-sm text-[var(--text-primary)] mb-1">3. Filter by size</div>
            <p className="text-xs text-[var(--text-secondary)]">Only transfers above your selected threshold are shown, sorted newest first, with a direct link to the block explorer for each.</p>
          </div>
        </div>

        <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4">
          Large wallet movements often precede volatility. A big transfer to an exchange can signal an intent to sell, while a transfer off an exchange into cold storage often signals long-term holding. Watching whale activity gives an early read on sentiment that price charts alone don't show.
        </p>
        <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
          Free-tier data limits currently restrict coverage to Ethereum, Polygon, and Arbitrum, tracking native currency and USDT/USDC transfers. Very fast-moving activity between refreshes may occasionally be missed. This tool is informational only and is not financial advice.
        </p>
      </div>
    </div>
  );
}